import { isUtagLoaded, LiveChatError } from '../../utils/common-utils';
import { EngagementKeys } from '@/common/engagementKeys';
import { buildChatDrawerTemplate } from './chat-drawer';
import {
  AnalyticsEvents,
  emitAnalyticsEvent,
  makePageEngagementAnalyticsEvent
} from '@/common/analytics';

const EngagementType = {
  ArtistDrawerButton: 'artist',
  SupportDrawerButton: 'support',
  PageEngagement: 'page'
} as const;

type EngagementType = (typeof EngagementType)[keyof typeof EngagementType];

const LocalStorageKeys = {
  userTryingAgain: 'livechat_user_trying_again',
  shouldLockState: 'livechat_should_lock_state',
  shouldSkipUnloadHandler: 'livechat_should_skip_unload_handler',
  selectedEngagementType: 'livechat_selected_engagement_type',
  selectedEngagementKey: 'livechat_selected_engagement_key'
} as const;

const LocalStorageStateLockValues = {
  errorState: 'error_state',
  utagMissingState: 'utag_missing_state'
};

type LocalStorageKey = (typeof LocalStorageKeys)[keyof typeof LocalStorageKeys];

const HtmlSelectors = {
  // Drawer
  chatDrawer: '.js-live-chat-drawer',
  engagementsDrawer: '.js-live-chat-drawer-success',
  cookieWarningDrawer: '.js-live-chat-drawer-cookie-warning',
  warningDrawer: '.js-live-chat-drawer-warning',
  headerIcon: '.js-live-chat-header-icon',

  // Buttons
  artistButton: '.js-live-chat-drawer-artist-button',
  supportButton: '.js-live-chat-drawer-support-button',
  floatingButton: '.js-live-chat-button',
  floatingButtonIcon: '.live-chat-button__icon',
  drawerCloseButton: '.js-live-chat-drawer-close',
  floatingButtonLoading: '.js-live-chat-button-loading',
  cookieButton: '.js-live-chat-drawer-cookie-button',
  refreshWindowButton: '.js-live-chat-drawer-refresh-window-button',

  // Etc
  oneTrustPopUp: '#onetrust-consent-sdk'
} as const;

type HtmlSelectorKeys = keyof typeof HtmlSelectors;

type EngagementTypeHandler = {
  button: Element | null;
  callback: () => void;
};

const engagementHandlers: Record<EngagementType, EngagementTypeHandler> = {
  artist: {
    button: null, // Set during initialization
    callback: () => {
      window.LiveChat!.onBeautyChatButtonClick?.();
      emitAnalyticsEvent(AnalyticsEvents.BeautyEngagementButtonClicked);
    }
  },
  support: {
    button: null, // Set during initialization
    callback: () => {
      window.LiveChat!.onSupportChatButtonClick?.();
      emitAnalyticsEvent(AnalyticsEvents.SupportEngagementButtonClicked);
    }
  },
  page: {
    button: null, // Always null
    callback: () => window.LiveChat!.onPageEngagementClick?.()
    // Page Engagement analytics are handled in window.LiveChat.open()
  }
};

type State = {
  name: string;
  enter: () => void;
  exit?: () => void;
  isClosed?: boolean;
  isLoading?: boolean;
};

type OpenStateKeys =
  | 'idle'
  | 'loadingSupport'
  | 'loadingBeauty'
  | 'loadingPage'
  | 'cookie'
  | 'utagMissing'
  | 'error';

type ClosedStateKeys = 'idle' | 'loading';

type UIStatesType = {
  open: Record<OpenStateKeys, State>;
  closed: Record<ClosedStateKeys, State>;
};

const htmlElements = {
  chatContainer: document.getElementById('live-chat-ui-container'),
  ...Object.keys(HtmlSelectors).reduce(
    (acc, key) => {
      acc[key as HtmlSelectorKeys] = null;
      return acc;
    },
    {} as Record<HtmlSelectorKeys, Element | null>
  )
};

type LiveChatUI = {
  init: () => Promise<void>;
};

export function makeLiveChatUI(): LiveChatUI {
  const UIStates: UIStatesType = {
    closed: {
      idle: {
        name: 'Closed_Idle',
        enter: () => {
          hideElement(htmlElements.chatDrawer!);
          document.removeEventListener('click', handleOutsideClick);
          hideDrawerEngagementsLoading();
        },
        exit: () => {
          document.addEventListener('click', handleOutsideClick);
        },
        isClosed: true
      },
      loading: {
        name: 'Closed_Loading',
        enter: () => {
          showFloatingButtonLoadingState();
          hideElement(htmlElements.chatDrawer!);
          disableEngagementButtons();
          document.removeEventListener('click', handleOutsideClick);
          hideDrawerEngagementsLoading();
        },
        exit: () => {
          hideFloatingButtonLoadingState();
          enableEngagementButtons();
          document.addEventListener('click', handleOutsideClick);
        },
        isClosed: true,
        isLoading: true
      }
    },
    open: {
      idle: {
        name: 'Open_Idle',
        enter: () => {
          setActiveDrawer(htmlElements.engagementsDrawer!);
          showElement(htmlElements.chatDrawer!);
        }
      },
      loadingSupport: {
        name: 'Open_Loading_Support',
        enter: () => {
          setActiveDrawer(htmlElements.engagementsDrawer!);
          showElement(htmlElements.chatDrawer!);
          disableEngagementButtons();
          showDrawerEngagementLoading(EngagementType.SupportDrawerButton);
        },
        exit: () => {
          hideDrawerEngagementsLoading();
          enableEngagementButtons();
        },
        isLoading: true
      },
      loadingBeauty: {
        name: 'Open_Loading_Beauty',
        enter: () => {
          setActiveDrawer(htmlElements.engagementsDrawer!);
          showElement(htmlElements.chatDrawer!);
          disableEngagementButtons();
          showDrawerEngagementLoading(EngagementType.ArtistDrawerButton);
        },
        exit: () => {
          hideDrawerEngagementsLoading();
          enableEngagementButtons();
        },
        isLoading: true
      },
      loadingPage: {
        name: 'Open_Loading_Page',
        enter: () => {
          setActiveDrawer(htmlElements.engagementsDrawer!);
          showElement(htmlElements.chatDrawer!);
          disableEngagementButtons();
        },
        exit: () => {
          hideDrawerEngagementsLoading();
          enableEngagementButtons();
        },
        isLoading: true
      },
      cookie: {
        name: 'Open_Cookie_Warning',
        enter: () => {
          setActiveDrawer(htmlElements.cookieWarningDrawer!);
          showElement(htmlElements.cookieButton!);
          showElement(htmlElements.chatDrawer!);
        }
      },
      utagMissing: {
        name: 'Open_Utag_Missing_Warning',
        enter: () => {
          setActiveDrawer(htmlElements.cookieWarningDrawer!);
          hideElement(htmlElements.cookieButton!);
          showElement(htmlElements.chatDrawer!);
        }
      },
      error: {
        name: 'Open_Error',
        enter: () => {
          setActiveDrawer(htmlElements.warningDrawer!);
          showElement(htmlElements.chatDrawer!);
        }
      }
    }
  };

  const api = window.LiveChat;
  const states: UIStatesType = UIStates;
  let activeState: State = isUserTryingAgain() ? UIStates.closed.loading : UIStates.closed.idle;
  let loadingEngagementType: EngagementType | null = null;
  let isCookieError = false;

  function transition(newState: State): void {
    const previousState = activeState;
    previousState.exit?.();
    activeState = newState;
    activeState.enter();
  }

  async function init(): Promise<void> {
    try {
      // Pre-init checks
      if (!htmlElements.chatContainer) {
        throw new LiveChatError('LiveChat UI container not found');
      }

      if (!api?.manifest || !api?.resources) {
        throw new Error('Missing required properties in LiveChat API');
      }

      const { manifest, resources } = api;
      if (!resources.ui.enabled || !isManifestValid(manifest)) return;

      const chatUiTemplate = await buildChatDrawerTemplate(resources.ui);
      htmlElements.chatContainer.insertAdjacentHTML('afterbegin', chatUiTemplate);

      collectDOMElements();
      initEventListeners();
      initCookieWindow();
      initHashListener();

      const type = getLocalStorageItem(LocalStorageKeys.selectedEngagementType) as EngagementType;
      if (isUserTryingAgain()) {
        transition(UIStates.closed.loading);
        const key = getLocalStorageItem(LocalStorageKeys.selectedEngagementKey) as EngagementKey;
        if (type) {
          await selectEngagement(type, key);
        }
      } else {
        transition(UIStates.closed.idle);
        if (type) {
          removeLocalStorageItem(LocalStorageKeys.selectedEngagementType);
          removeLocalStorageItem(LocalStorageKeys.selectedEngagementKey);
        }
      }

      // Load handlers for Engagement Drawer buttons
      engagementHandlers.artist.button = htmlElements.artistButton;
      engagementHandlers.support.button = htmlElements.supportButton;

      window.LiveChat!.open = (engagementKey: EngagementKey) => {
        const lockState = getLockState();
        if (lockState) {
          performLockStateTransition(lockState);
        } else {
          transition(UIStates.closed.loading);
          selectEngagement(EngagementType.PageEngagement, engagementKey);
          emitAnalyticsEvent(makePageEngagementAnalyticsEvent(engagementKey));
        }
      };
    } catch (error: unknown) {
      loadingEngagementType = null;
      setLocalStorageItem(LocalStorageKeys.shouldLockState, LocalStorageStateLockValues.errorState);
      transition(states.open.error);
      throw new LiveChatError(`${(error as Error).message}`);
    }
  }

  async function selectEngagement(type: EngagementType, key?: EngagementKey): Promise<void> {
    try {
      setLocalStorageItem(LocalStorageKeys.selectedEngagementType, type);
      if (key) {
        setLocalStorageItem(LocalStorageKeys.selectedEngagementKey, key);
      }

      // We wait for utag to be ready before checking if it allows functional cookies.
      transition(states.closed.loading);
      const utagLoaded = await isUtagLoaded();
      if (!utagLoaded) {
        clearLocalStorage();
        transition(states.open.utagMissing);
        setLocalStorageItem(
          LocalStorageKeys.shouldLockState,
          LocalStorageStateLockValues.utagMissingState
        );
        return;
      }

      // We wait for optanon to be ready before checking if it allows functional cookies.
      const cookiesAllowed = await isCookiesAllowed();
      if (!cookiesAllowed) {
        isCookieError = true;
        transition(states.open.cookie);
        return;
      } else {
        isCookieError = false;
      }

      const chatOrcData = mapEngagementTypeToOrchestratorData(type);
      if (!chatOrcData) {
        loadingEngagementType = null;
        setLocalStorageItem(
          LocalStorageKeys.shouldLockState,
          LocalStorageStateLockValues.errorState
        );
        transition(states.open.error);
        throw new LiveChatError(`chatOrcData is undefined for engagement type: ${type}`);
      }
      onIntentLoopEvents(chatOrcData);
      // If user is trying again, do not tranistion to drawer open, keep closed loading.
      if (!isUserTryingAgain()) {
        performEngagementTransition(type)(chatOrcData, key);
      }
      loadingEngagementType = type;

      const handler = engagementHandlers[type];
      if (!handler) {
        throw new Error(`❌ No handler found for "${type}"`);
      }
      handler.callback();
    } catch (error: unknown) {
      loadingEngagementType = null;
      setLocalStorageItem(LocalStorageKeys.shouldLockState, LocalStorageStateLockValues.errorState);
      transition(states.open.error);
      throw new LiveChatError(`${(error as Error).message}`);
    }
  }

  async function onCookiesUpdated(): Promise<void> {
    // We wait optanon to be ready, before checking if it allows functional cookies.
    const cookiesAllowed = await isCookiesAllowed();
    if (!cookiesAllowed) return;

    const type = getLocalStorageItem(LocalStorageKeys.selectedEngagementType);
    const key = getLocalStorageItem(LocalStorageKeys.selectedEngagementKey);
    if (type) {
      selectEngagement(
        type as EngagementType,
        key && key in EngagementKeys ? (key as EngagementKey) : undefined
      );
    }
  }

  function initEventListeners(): void {
    window.addEventListener('OneTrustGroupsUpdated', onCookiesUpdated);

    htmlElements.floatingButton!.addEventListener('click', onFloatingButtonClick);

    htmlElements.artistButton!.addEventListener('click', () => {
      selectEngagement(EngagementType.ArtistDrawerButton);
    });

    htmlElements.supportButton!.addEventListener('click', () => {
      selectEngagement(EngagementType.SupportDrawerButton);
    });

    htmlElements.refreshWindowButton!.addEventListener('click', () => {
      setLocalStorageItem(LocalStorageKeys.userTryingAgain, 'true');
      setLocalStorageItem(LocalStorageKeys.shouldSkipUnloadHandler, 'true');
      removeLocalStorageItem(LocalStorageKeys.shouldLockState);
      location.reload();
    });

    htmlElements.drawerCloseButton!.addEventListener('click', () => {
      // Two identical transitions will be triggered when this is clicked during engagement load. This is expected behavior.
      window.LiveChat!.onCloseButtonClick?.();
      emitAnalyticsEvent(AnalyticsEvents.XButtonClicked);
      transition(UIStates.closed.idle);
    });

    window.addEventListener('beforeunload', () => {
      if (!getLocalStorageItem(LocalStorageKeys.shouldSkipUnloadHandler)) {
        clearLocalStorage();
      }
    });
  }

  function onIntentLoopEvents(chatOrcData: ChatOrcData): void {
    document.addEventListener(chatOrcData.intentLoopFinishedEventName, () => {
      if (!chatOrcData.isUserAwaitingChat) return;
      loadingEngagementType = null;
      transition(states.closed.idle);
      clearLocalStorage();
    });

    document.addEventListener(chatOrcData.errorMessageEventName, () => {
      loadingEngagementType = null;
      setLocalStorageItem(LocalStorageKeys.shouldLockState, LocalStorageStateLockValues.errorState);
      transition(states.open.error);
    });
  }

  function initHashListener(): void {
    // URL Hash Listener for Page Engagements
    document.querySelectorAll('a[href^="#livechat_"]').forEach((el) => {
      el.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const anchor = (e.target as HTMLElement).closest('a');
        if (!anchor) return;
        const key = parseUrlForEngagementKey(anchor.hash);
        if (key) {
          window.LiveChat?.open?.(key);
        }
      });
    });

    // URL Hash Listener - fallback
    window.addEventListener('hashchange', () => {
      const key = parseUrlForEngagementKey(location.hash);
      if (key) {
        window.LiveChat?.open?.(key);
        const newHash = location.hash.replace(/#livechat_[^#]*/g, '');
        history.replaceState(null, '', `${location.pathname}${location.search}${newHash}`);
      }
    });
  }

  function initCookieWindow(): void {
    htmlElements.cookieButton!.classList.add('ot-sdk-show-settings');
    htmlElements.cookieButton!.id = 'ot-sdk-btn';
    htmlElements.cookieButton!.addEventListener('click', () =>
      window.OneTrust?.ToggleInfoDisplay()
    );
  }

  function performEngagementTransition(
    type: EngagementType
  ): (data: ChatOrcData, key?: EngagementKey) => void {
    return {
      [EngagementType.ArtistDrawerButton]: () => {
        transition(UIStates.open.loadingBeauty);
      },
      [EngagementType.SupportDrawerButton]: () => {
        transition(UIStates.open.loadingSupport);
      },
      [EngagementType.PageEngagement]: (data: ChatOrcData, key?: EngagementKey) => {
        // When LiveChat.open() is called, EngagementType.PageEngagement is used. An engagementKey is expected to be provided as an argument to open().
        // If an engagementKey is provided, and it is valid, it will override the chatOrcData engKey, and use it for the LivePerson click.
        // Otherwise, the default engKey for the EngagementType will be used, and a warning will be thrown. We want to discourage using the default,
        // as it will impact LivePerson engagement metrics.
        if (key && Object.prototype.hasOwnProperty.call(EngagementKeys, key)) {
          data.engKey = key;
        } else {
          console.warn(
            `[LiveChat]: Incorrect EngagementKey provided: ${key}. There's no LivePerson Div ID on the page corresponding to that EngagementKey. Falling back to the default page div.`
          );
        }
        transition(UIStates.closed.loading);
      }
    }[type];
  }

  function performLockStateTransition(lockState: string) {
    switch (lockState) {
      case LocalStorageStateLockValues.errorState:
        transition(UIStates.open.error);
        return;
      case LocalStorageStateLockValues.utagMissingState:
        transition(UIStates.open.utagMissing);
        return;
      default:
        break;
    }
  }

  function onFloatingButtonClick(): void {
    if (activeState.isClosed) {
      emitAnalyticsEvent(AnalyticsEvents.FloatingButtonClickedOpen); // Drawer is closed so the "Open" event is firing
    } else {
      emitAnalyticsEvent(AnalyticsEvents.FloatingButtonClickedClose); // Drawer is open so the "Close" event is firing
    }

    if (isCookieError && activeState === UIStates.closed.idle) {
      transition(UIStates.open.cookie);
      return;
    }

    const lockState: string | null = getLockState();

    if (
      lockState &&
      (activeState === UIStates.closed.idle || activeState === UIStates.closed.loading)
    ) {
      performLockStateTransition(lockState);
      return;
    }

    if (activeState === UIStates.closed.idle) {
      transition(UIStates.open.idle);
      return;
    }

    if (activeState === UIStates.closed.loading) {
      if (loadingEngagementType) {
        transition(
          {
            [EngagementType.ArtistDrawerButton]: UIStates.open.loadingBeauty,
            [EngagementType.SupportDrawerButton]: UIStates.open.loadingSupport,
            [EngagementType.PageEngagement]: UIStates.open.loadingPage
          }[loadingEngagementType]
        );
      }
      return;
    }

    if (
      activeState === UIStates.open.idle ||
      activeState === UIStates.open.cookie ||
      activeState === UIStates.open.error
    ) {
      transition(UIStates.closed.idle);
      return;
    }

    if (
      activeState === UIStates.open.loadingBeauty ||
      activeState === UIStates.open.loadingSupport ||
      activeState === UIStates.open.loadingPage
    ) {
      transition(UIStates.closed.loading);
      return;
    }
  }

  function handleOutsideClick(event: MouseEvent) {
    const oneTrustPopUp = document.querySelector(HtmlSelectors.oneTrustPopUp);

    if (
      !(
        event.target instanceof HTMLAnchorElement &&
        event.target.getAttribute('href')?.startsWith('#livechat_')
      ) &&
      !htmlElements.chatDrawer!.contains(event.target as Node) &&
      !htmlElements.floatingButton!.contains(event.target as Node) &&
      (!oneTrustPopUp || !oneTrustPopUp.contains(event.target as Node))
    ) {
      transition(activeState.isLoading ? UIStates.closed.loading : UIStates.closed.idle);
    }
  }

  return { init };
}

makeLiveChatUI().init();

function collectDOMElements(): void {
  Object.entries(HtmlSelectors).forEach(([key, selector]) => {
    const el = htmlElements.chatContainer!.querySelector(selector);
    const ignoredSelectors: string[] = [HtmlSelectors.oneTrustPopUp];
    if (!el && !ignoredSelectors.includes(selector)) {
      throw new Error(`Missing UI element for ${key}`);
    }
    htmlElements[key as HtmlSelectorKeys] = el;
  });
}

function toggleDrawerEngagementLoadingState(
  type: EngagementType,
  isLoading: boolean,
  button: Element | null
): void {
  if (!button) return;
  button.querySelector(`.js-${type}-text`)?.classList.toggle('live-chat-hidden', isLoading);
  button.querySelector(`.js-${type}-loading`)?.classList.toggle('live-chat-hidden', !isLoading);
  button
    .querySelector(`.js-${type}-loading-text`)
    ?.classList.toggle('live-chat-hidden', !isLoading);
}

function mapEngagementTypeToButton(type: Exclude<EngagementType, 'page'>): Element | null {
  return {
    [EngagementType.ArtistDrawerButton]: htmlElements.artistButton,
    [EngagementType.SupportDrawerButton]: htmlElements.supportButton
  }[type];
}

function showDrawerEngagementLoading(type: Exclude<EngagementType, 'page'>) {
  const button = mapEngagementTypeToButton(type);
  toggleDrawerEngagementLoadingState(type, true, button);
}

function hideDrawerEngagementLoading(type: Exclude<EngagementType, 'page'>) {
  const button = mapEngagementTypeToButton(type);
  toggleDrawerEngagementLoadingState(type, false, button);
}

function hideDrawerEngagementsLoading(): void {
  hideDrawerEngagementLoading(EngagementType.ArtistDrawerButton);
  hideDrawerEngagementLoading(EngagementType.SupportDrawerButton);
}

function parseUrlForEngagementKey(value: string): EngagementKey | null {
  const strValue = value.split('#').find((v) => v.startsWith('livechat_')) ?? null;
  return strValue && strValue in EngagementKeys ? (strValue as EngagementKey) : null;
}

function isUserTryingAgain(): boolean {
  return getLocalStorageItem(LocalStorageKeys.userTryingAgain) === 'true';
}

async function isCookiesAllowed(): Promise<boolean> {
  if (window.OptanonActiveGroups) {
    return window.OptanonActiveGroups?.indexOf('3') !== -1;
  }

  const timeout = 15000;
  const interval = 100;
  const start = Date.now();

  return new Promise((resolve) => {
    const checkOptanon = () => {
      if (window.OptanonActiveGroups) {
        resolve(window.OptanonActiveGroups?.indexOf('3') !== -1);
      } else if (Date.now() - start >= timeout) {
        resolve(false);
      } else {
        setTimeout(checkOptanon, interval);
      }
    };

    checkOptanon();
  });
}

function getLockState(): string | null {
  return getLocalStorageItem(LocalStorageKeys.shouldLockState);
}

function isManifestValid(manifest: Manifest): boolean {
  return Array.isArray(manifest.ui) && manifest.ui.length > 0;
}

function showElement(el: Element): void {
  el.classList.remove('live-chat-hidden');
}

function hideElement(el: Element): void {
  el.classList.add('live-chat-hidden');
}

function setActiveDrawer(el: Element): void {
  const templates = [
    htmlElements.engagementsDrawer!,
    htmlElements.warningDrawer!,
    htmlElements.cookieWarningDrawer!
  ];

  templates.forEach((template) => {
    const actionOnElement = template !== el ? hideElement : showElement;
    actionOnElement(template);
  });

  updateDrawerHeaderState();
}

function setEngagementButtonsDisabled(disabling: boolean): void {
  const artistButton = htmlElements.chatContainer!.querySelector(HtmlSelectors.artistButton);
  if (artistButton && artistButton instanceof HTMLButtonElement) {
    artistButton.disabled = disabling;
  }

  const supportButton = htmlElements.chatContainer!.querySelector(HtmlSelectors.supportButton);
  if (supportButton && supportButton instanceof HTMLButtonElement) {
    supportButton.disabled = disabling;
  }
}

function disableEngagementButtons(): void {
  setEngagementButtonsDisabled(true);
}

function enableEngagementButtons(): void {
  setEngagementButtonsDisabled(false);
}

function updateDrawerHeaderState(): void {
  const hasVisibleWarnings =
    !doesElementHaveClass(htmlElements.warningDrawer!, 'live-chat-hidden') ||
    !doesElementHaveClass(htmlElements.cookieWarningDrawer!, 'live-chat-hidden');

  htmlElements.headerIcon!.classList.toggle(
    'live-chat-drawer__header-icon--content',
    !hasVisibleWarnings
  );
  htmlElements.headerIcon!.classList.toggle(
    'live-chat-drawer__header-icon--warning',
    hasVisibleWarnings
  );
}

function doesElementHaveClass(el: Element, className: string): boolean {
  return el.classList.contains(className);
}

function showFloatingButtonLoadingState(): void {
  showElement(htmlElements.floatingButtonLoading!);
  hideElement(htmlElements.floatingButtonIcon!);
}

function hideFloatingButtonLoadingState(): void {
  hideElement(htmlElements.floatingButtonLoading!);
  showElement(htmlElements.floatingButtonIcon!);
}

function mapEngagementTypeToOrchestratorData(type: EngagementType): ChatOrcData | undefined {
  return {
    [EngagementType.ArtistDrawerButton]: window.LiveChat!.lpBeautyChatOrcData,
    [EngagementType.SupportDrawerButton]: window.LiveChat!.lpSupportChatOrcData,
    [EngagementType.PageEngagement]: window.LiveChat!.lpPageChatOrcData
  }[type];
}

function setLocalStorageItem<T>(key: LocalStorageKey, value: T): void {
  localStorage?.setItem(key, value as string);
}

function getLocalStorageItem(key: LocalStorageKey): string | null {
  return localStorage?.getItem(key) ?? null;
}

function removeLocalStorageItem(key: LocalStorageKey): void {
  localStorage?.removeItem(key);
}

function clearLocalStorage(): void {
  Object.values(LocalStorageKeys).forEach((val) => removeLocalStorageItem(val));
}
